import { PanelLeftClose, PanelLeftOpen } from "lucide-react";
import { cn } from "@/lib/utils";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip";

type SidebarCollapseToggleProps = {
  collapsed: boolean;
  onToggle: () => void;
  /** "header" is the icon-only button beside the logo; "footer" is the full-width bottom row. */
  variant: "header" | "footer";
};

const FOCUS_RING_CLASSES =
  "focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2";

export function SidebarCollapseToggle({
  collapsed,
  onToggle,
  variant,
}: SidebarCollapseToggleProps) {
  const label = collapsed ? "Expand sidebar" : "Collapse sidebar";
  const Icon = collapsed ? PanelLeftOpen : PanelLeftClose;
  const isHeader = variant === "header";
  // Footer text only shows expanded; the header button is always icon-only.
  const showText = !isHeader && !collapsed;

  const button = (
    <button
      type="button"
      onClick={onToggle}
      aria-label={label}
      aria-expanded={!collapsed}
      className={cn(
        "transition-colors hover:bg-slate-100 hover:text-slate-900",
        FOCUS_RING_CLASSES,
        isHeader
          ? "flex size-9 shrink-0 items-center justify-center rounded-lg text-slate-500"
          : "flex min-h-12 w-full items-center gap-3 rounded-xl border border-transparent px-3 py-2 text-sm font-medium text-slate-600 hover:bg-slate-50",
        !isHeader && collapsed && "justify-center px-0",
      )}
    >
      <Icon className="size-5 shrink-0 text-slate-500" />
      {showText && <span>Collapse</span>}
    </button>
  );

  // Tooltip whenever the visible control has no text.
  const toggle = showText ? (
    button
  ) : (
    <Tooltip>
      <TooltipTrigger asChild>{button}</TooltipTrigger>
      <TooltipContent side={isHeader ? "bottom" : "right"}>
        {label}
      </TooltipContent>
    </Tooltip>
  );

  if (isHeader) {
    return toggle;
  }
  return (
    <div className="shrink-0 border-t border-slate-200 px-3 py-3">{toggle}</div>
  );
}
